import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../../lib/api";
import { PageHeader } from "../../components/StaffLayout";
import { Modal, Inp, Sel } from "./Enquiries";
import { Plus, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Patients() {
  const [items, setItems] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [q, setQ] = useState("");
  const [showNew, setShowNew] = useState(false);

  const load = async (qs = "") => {
    const { data } = await api.get(`/patients${qs ? `?q=${encodeURIComponent(qs)}` : ""}`);
    setItems(data);
  };
  useEffect(() => {
    load();
    api.get("/doctors").then(r => setDoctors(r.data));
  }, []);

  return (
    <div data-testid="patients-page">
      <PageHeader
        title="Patient Directory"
        subtitle={`${items.length} patient${items.length === 1 ? "" : "s"}`}
        actions={
          <button onClick={() => setShowNew(true)}
            className="inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#CC0000] text-white font-semibold rounded-sm px-4 py-2 text-sm"
            data-testid="add-patient-btn">
            <Plus size={16} weight="bold"/> New patient
          </button>
        }
      />
      <div className="px-6 sm:px-10 py-8">
        <div className="flex items-center mb-4">
          <div className="relative w-full max-w-md">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"/>
            <input value={q} onChange={(e)=>setQ(e.target.value)}
              onKeyDown={(e)=> e.key==="Enter" && load(q)}
              placeholder="Search name / phone / email"
              className="w-full bg-white border border-zinc-300 rounded-sm pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-zinc-900"
              data-testid="patient-search"/>
          </div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200">
                <Th>Name</Th><Th>Phone</Th><Th>Email</Th><Th>DOB</Th><Th>Gender</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <Td><div className="font-semibold text-zinc-900">{p.name}</div></Td>
                  <Td>{p.phone}</Td>
                  <Td className="text-zinc-500">{p.email || "—"}</Td>
                  <Td>{p.dob || "—"}</Td>
                  <Td className="capitalize">{p.gender || "—"}</Td>
                  <Td>
                    <Link to={`/staff/patients/${p.id}`} className="text-[#E60000] font-semibold text-xs uppercase tracking-[0.2em]" data-testid={`patient-view-${p.id}`}>Open ›</Link>
                  </Td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={6} className="text-center text-zinc-500 py-12 text-sm">No patients yet. Add the first one.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showNew && <NewPatient doctors={doctors} onClose={()=>setShowNew(false)} onSaved={()=>{ setShowNew(false); load(); }}/>}
    </div>
  );
}

function Th({ children }) { return <th className="text-left text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-700 px-4 py-3">{children}</th>; }
function Td({ children, className="" }) { return <td className={`px-4 py-3 ${className}`}>{children}</td>; }

function NewPatient({ doctors = [], onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", dob: "", gender: "male", address: "", blood_group: "", allergies: "", medical_history: "", primary_doctor_id: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post("/patients", { ...form, email: form.email || null, primary_doctor_id: form.primary_doctor_id || null });
      toast.success("Patient added");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} title="New patient">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Inp label="Name" v={form.name} onChange={(v)=>setForm({...form,name:v})} testid="np-name"/>
        <Inp label="Phone" v={form.phone} onChange={(v)=>setForm({...form,phone:v})} testid="np-phone"/>
        <Inp label="Email" v={form.email} onChange={(v)=>setForm({...form,email:v})} testid="np-email"/>
        <Inp label="DOB" type="date" v={form.dob} onChange={(v)=>setForm({...form,dob:v})} testid="np-dob"/>
        <Sel label="Gender" v={form.gender} onChange={(v)=>setForm({...form,gender:v})}
             opts={[["male","Male"],["female","Female"],["other","Other"]]} testid="np-gender"/>
        <Inp label="Blood group" v={form.blood_group} onChange={(v)=>setForm({...form,blood_group:v})} testid="np-blood"/>
        <div className="sm:col-span-2">
          <label className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-700">Primary doctor</label>
          <select value={form.primary_doctor_id} onChange={(e)=>setForm({...form, primary_doctor_id: e.target.value})}
            className="mt-2 w-full border border-zinc-300 rounded-sm px-3 py-2 bg-white focus:outline-none focus:border-zinc-900" data-testid="np-doctor">
            <option value="">— assign later —</option>
            {doctors.map(d => <option key={d.id} value={d.id}>{d.name} {d.specialization?`· ${d.specialization}`:""}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2"><Inp label="Address" v={form.address} onChange={(v)=>setForm({...form,address:v})} testid="np-address"/></div>
        <div className="sm:col-span-2"><Inp label="Allergies" v={form.allergies} onChange={(v)=>setForm({...form,allergies:v})} testid="np-allergies"/></div>
        <div className="sm:col-span-2">
          <label className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-700">Medical history</label>
          <textarea rows={3} value={form.medical_history} onChange={(e)=>setForm({...form,medical_history:e.target.value})}
            className="mt-2 w-full border border-zinc-300 rounded-sm px-3 py-2 focus:outline-none focus:border-zinc-900" data-testid="np-history"/>
        </div>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onClose} className="bg-transparent border border-zinc-900 text-zinc-900 font-semibold rounded-sm px-4 py-2 text-sm">Cancel</button>
        <button onClick={save} disabled={busy} className="bg-[#E60000] hover:bg-[#CC0000] text-white font-semibold rounded-sm px-4 py-2 text-sm disabled:opacity-60" data-testid="np-save">
          {busy ? "Saving…" : "Add patient"}
        </button>
      </div>
    </Modal>
  );
}
